import React, { useState, useRef, useEffect } from "react";
import { useTranslate } from 'react-redux-multilingual'
import {Badge} from 'react-bootstrap';

/**
 * Vignette raccourci caisse.
 * Affiche une tuile cliquable représentant un article, une famille, une marque, etc.
 * En mode config admin (origin='config'), un menu ⋮ apparaît avec les options disponibles
 * (suppression, modification couleur) selon les callbacks fournis.
 *
 * @param {string}   title            - Libellé affiché sur la vignette
 * @param {string}   image            - URL image (articles uniquement)
 * @param {string}   raccourciType    - Type : 'article', 'firstlevelcat', 'giftcard', 'fullCustom', …
 * @param {Function} raccourciAction  - Callback de navigation/action
 * @param {string|number} raccourciTypeId - Identifiant de l'élément ciblé
 * @param {boolean}  disabled         - Désactive les interactions
 * @param {string}   origin           - 'config' pour le contexte administration, sinon POS
 * @param {string}   color            - Couleur de fond hexadécimale
 * @param {Function} [onDelete]       - Callback de suppression
 * @param {Function} [onColorEdit]    - Callback d'édition de couleur
 */
const Raccourci = (props) => {
    const t = useTranslate()

    // ─── Menu ⋮ ───────────────────────────────────────────────────────────────
    const [menuOpen, setMenuOpen] = useState(false)
    const menuRef = useRef(null)
    const hasMenu = props.origin === 'config' && (props.onDelete || props.onColorEdit || props.onNameEdit)

    // Ferme le menu si clic en dehors
    useEffect(() => {
        if (!menuOpen) return
        const close = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false)
        }
        document.addEventListener('mousedown', close)
        return () => document.removeEventListener('mousedown', close)
    }, [menuOpen])

    // ─── Handlers ─────────────────────────────────────────────────────────────
    const handleClick = (title) => {
        if (title === 'empty') return // tuile "Aucun article" : pas d'action possible
        if (props.origin === 'config' && (title === 'article' || title === 'giftcard')) return
        if (title === 'giftcard') {
            props.raccourciAction(title, false, false)
        } else if (title === 'article') {
            props.raccourciAction(title, props.raccourciTypeId, false)
        } else {
            props.raccourciAction(title, false, props.raccourciTypeId)
        }
    }

    // Edition du RPPSL
    const handleClickRpPsl = () => {
        props.setShowModalEdit(true)
        props.setArticleIdModal(props.raccourciTypeId)
        props.setRppsl(props.rppsl)
        let rppslId = props.rppsl.find(value => value.article_id == props.raccourciTypeId)
        props.setRppslId(rppslId.id)
        props.setRpValue(rppslId.rp)
        props.setPslValue(rppslId.psl)
    }

    // Création To
    const handleClickTo = () => {
        props.setShowModalAddTo(true)
        props.setArticleIdModal(props.raccourciTypeId)
        props.setArticleNameModal(props.title)
        props.setArticleSkuModal(props.sku)
    }

    // ─── Render ───────────────────────────────────────────────────────────────
    let raccourciDisplayTitle = (props.raccourciType === 'empty') ? t('aucunArticleRaccourci') : (props.title ?? '')
    let raccourciTitle = raccourciDisplayTitle.toUpperCase()
    let raccourciColor = (props.raccourciType === 'giftcard')
        ? `col-12 ${!props.color ? 'bg-primary' : ''} rounded raccourciPos text-white`
        : (props.raccourciType === 'article')
            ? 'col-12 rounded raccourciPos text-white styleImgRaccourci'
            : (props.raccourciType === 'empty')
                ? 'col-12 bg-secondary rounded raccourciPos text-white'
                : `col-12 ${!props.color ? 'bg-info' : ''} rounded raccourciPos text-white`

    let badgeColor = (props.stockArticle && +props.stockArticle > 0) ? 'success' : 'danger'
    let badgeColorDock = (props.stockDock && +props.stockDock > 0) ? 'success' : 'danger'
    let badgeColorRp = (props.stockArticle && props.rppsl && props.rppsl.length > 0 && props.rppsl[0].rp <= props.stockArticle) ? 'success' : 'danger'
    let badgeColorPsl = 'white'
    let supStyle = (props.stockArticle && +props.stockArticle > 0) ? 'badgeStockRaccourci textDark' : 'badgeStockRaccourci'
    let supStyleDock = (props.stockDock && +props.stockDock > 0) ? 'badgeStockRaccourci textDark' : 'badgeStockRaccourci'
    let supStyleRp = (props.stockArticle && props.rppsl && props.rppsl.length > 0 && props.rppsl[0].rp <= props.stockArticle) ? 'badgeStockRaccourci textDark' : 'badgeStockRaccourci'
    let supStylePsl = 'badgeStockRaccourci textDark'

    return (
        <div className="raccourciTile" style={{padding: '1px'}}>
            {/* Menu ⋮ — uniquement en mode config avec au moins une action disponible */}
            {hasMenu &&
                <div className="welcome-store-menu" ref={menuRef}>
                    <button
                        className="welcome-store-menu-btn"
                        onClick={(e) => { e.stopPropagation(); setMenuOpen(o => !o) }}
                    >⋮</button>
                    {menuOpen &&
                        <div className="welcome-store-dropdown">
                            {props.onNameEdit &&
                                <button onClick={(e) => { e.stopPropagation(); setMenuOpen(false); props.onNameEdit() }}>
                                    {t('editName')}
                                </button>
                            }
                            {props.onColorEdit &&
                                <button onClick={(e) => { e.stopPropagation(); setMenuOpen(false); props.onColorEdit() }}>
                                    {t('editColor')}
                                </button>
                            }
                            {props.onDelete &&
                                <button onClick={(e) => { e.stopPropagation(); setMenuOpen(false); props.onDelete() }}>
                                    {t('btnDelete')}
                                </button>
                            }
                        </div>
                    }
                </div>
            }
            <div
                className={raccourciColor}
                style={props.color ? {backgroundColor: props.color} : {}}
                onClick={() => handleClick(props.raccourciType)}
            >
                <div className="textAlign" style={{width: '100%', height: '100px'}}>
                    {(props.raccourciType === 'article' && props.image != null) ?
                        <img src={props.image.split(';')[0]} alt={props.title} className="imgRaccourci"/>
                    :
                        raccourciTitle
                    }
                </div>
            </div>
            {(props.raccourciType === 'article' && props.image != null) &&
                <div className="itemTitle pointer text-center rounded-bottom" onClick={() => handleClick(props.raccourciType)}>
                    {raccourciTitle}
                </div>
            }
            {props.disabled &&
                <div className="disabled rounded"></div>
            }
            {props.modulePos?.params?.raccourci_caisse?.params?.stock &&
                <>
                    {(props.raccourciType === 'article' && props.stockArticle) &&
                        (props.modulePos?.params?.raccourci_caisse?.params?.to ?
                            <Badge pill bg={badgeColor} className={supStyle} onDoubleClick={() => handleClickTo()}>
                                {props.stockArticle}
                            </Badge>
                        :
                            <Badge pill bg={badgeColor} className={supStyle}>
                                {props.stockArticle}
                            </Badge>
                        )
                    }
                    {(props.raccourciType === 'article' && props.stockDock) &&
                        <Badge pill bg={badgeColorDock} className={supStyleDock} style={{top: '24%'}}>
                            {'d ' + props.stockDock}
                        </Badge>
                    }
                    {(props.raccourciType === 'article' && props.rppsl && props.rppsl.length > 0) &&
                        (props.modulePos?.params?.raccourci_caisse?.params?.rppsl &&
                            <Badge pill bg={badgeColorRp} className={supStyleRp} style={{top: '44%'}} onDoubleClick={() => handleClickRpPsl()}>
                                {'m ' + props.rppsl[0].rp}
                            </Badge>
                        )
                    }
                    {(props.raccourciType === 'article' && props.rppsl && props.rppsl.length > 0) &&
                        (props.modulePos?.params?.raccourci_caisse?.params?.rppsl &&
                            <Badge pill bg={badgeColorPsl} className={supStylePsl} style={{top: '65%'}} onDoubleClick={() => handleClickRpPsl()}>
                                {'M ' + props.rppsl[0].psl}
                            </Badge>
                        )
                    }
                </>
            }
        </div>
    )
}

export default Raccourci;
